import fetcher, { FleetPaginated, FleetQueryParams } from '../fetcher';
import {
	CustomerManagementInfoPayload,
	CustomerShareholderPayload,
} from './customer';
import {
	ApprovalHistory,
	CustomerBusinessDocument,
	CustomerLegalDocument,
	CustomerReferralDocument,
	ExistingFinancingLimitInformation,
	FilePayload,
	FinancingApplicationLimitInformation,
	MainInformation,
	ProspectBankStatement,
	ProspectEmploymentContract,
	ProspectFinancialReport,
	ProspectFinancialReportAnalysis,
	SurveyParticipant,
} from './types';

export const getFRMPraSurveyList = () => {
	return fetcher({
		api: '/surveys/frm/get-pra-survey-list',
		body: {},
	});
};

export type GetPraSurveyDetailRes = {
	approvalHistory: ApprovalHistory[];
	mainInformation: MainInformation;
	customerCompanyManagementInformation: CustomerManagementInfoPayload<'json'>[];
	customerCompanyShareholderInformation: CustomerShareholderPayload<'json'>[];
	customerLegalDocuments: CustomerLegalDocument[];
	customerReferralDocuments: CustomerLegalDocument[];
	customerBusinessDocuments: CustomerBusinessDocument[];
	prospectBankStatements: ProspectBankStatement[];
	employmentContracts: ProspectEmploymentContract[];
	prospectFinancialReports: ProspectFinancialReport[];
};

export const getPraSurveyDetail = (prospectId: string) => {
	return fetcher<GetPraSurveyDetailRes>({
		api: '/surveys/get-pra-survey-detail',
		body: {
			prospectId,
		},
	});
};

export type SurveyInformation = {
	id?: number;
	department?: string;
	officeAddress: string;
	officeLat: number;
	officeLong: number;
	surveyOfficeDate: string;
	siteAddress: string;
	siteLat: number;
	siteLong: number;
	surveySiteDate: string;
	surveyParticipants: {
		userId?: number;
		name: string;
		position: string;
	}[];
};

export type GetSurveyAnalysisDetailRes = {
	mainInformation: MainInformation;
	prospectInformation: {
		existingFinancingLimitInformation: ExistingFinancingLimitInformation[];
		financingApplicationLimitInformation: FinancingApplicationLimitInformation[];
	};
	customerLegalDocuments: CustomerLegalDocument[];
	customerBusinessDocuments: CustomerBusinessDocument[];
	customerReferralDocuments?: CustomerReferralDocument[];
	employmentContracts: ProspectEmploymentContract[];
	prospectBankStatements: ProspectBankStatement[];
	prospectFinancialReports: ProspectFinancialReport[];
	prospectFinancialReportAnalysis: ProspectFinancialReportAnalysis[];
	surveyInformation: SurveyInformation;
	approvalHistories: ApprovalHistory[];
};

export const getSurveyAnalysisDetail = (prospectId: string) => {
	return fetcher<GetSurveyAnalysisDetailRes>({
		api: '/surveys/get-survey-analysis-detail',
		body: {
			prospectId,
		},
	});
};

export type SubmitPraSurveySupportingDocumentPayload = {
	prospectId: string;
	customerLegalDocuments: CustomerLegalDocument<'payload'>[];
	customerBusinessDocuments: CustomerBusinessDocument<'payload'>[];
	customerReferralDocuments: CustomerReferralDocument<'payload'>[];
	isSurveyByBankMandiri?: boolean;
	bankMandiriSurveyAt?: string;
	bankMandiriSurveyDocument?: FilePayload;
};

export const submitPraSurveySupportingDocument = (
	body: SubmitPraSurveySupportingDocumentPayload,
) => {
	return fetcher({
		api: '/surveys/frm/submit-pra-survey-supporting-document',
		body,
	});
};

export const addFinancialReport = (
	body: ProspectFinancialReport<'payload'>,
) => {
	return fetcher<ProspectFinancialReport>({
		api: '/surveys/frm/add-financial-report',
		body,
	});
};

export const updateFinancialReport = (
	body: ProspectFinancialReport<'payload'>,
) => {
	return fetcher({
		api: '/surveys/frm/update-financial-report',
		body,
	});
};

export const deleteFinancialReport = (reportId: number) => {
	return fetcher({
		api: '/surveys/frm/delete-financial-report',
		body: {
			reportId,
		},
	});
};

export const addEmploymentContract = (
	body: ProspectEmploymentContract<'payload'>,
) => {
	return fetcher<ProspectEmploymentContract>({
		api: '/surveys/frm/add-employment-contract',
		body,
	});
};

export const addEmploymentContractVerification = (
	body: ProspectEmploymentContract<'payload'>,
) => {
	return fetcher<ProspectEmploymentContract>({
		api: '/visit-reports/cca/add-employment-contract',
		body,
	});
};

export const updateEmploymentContract = (
	body: ProspectEmploymentContract<'payload'>,
) => {
	return fetcher({
		api: '/surveys/frm/update-employment-contract',
		body,
	});
};

export const updateEmploymentContractVerification = (
	body: ProspectEmploymentContract<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-employment-contract',
		body,
	});
};

export const deleteEmploymentContract = (employmentContractId: number) => {
	return fetcher({
		api: '/surveys/frm/delete-employment-contract',
		body: {
			employmentContractId,
		},
	});
};

export const deleteEmploymentContractVerification = (
	employmentContractId: number,
) => {
	return fetcher({
		api: '/visit-reports/cca/delete-employment-contract',
		body: {
			employmentContractId,
		},
	});
};

export const addBankStatement = (body: ProspectBankStatement<'payload'>) => {
	return fetcher<number>({
		api: '/surveys/frm/add-bank-statement',
		body,
	});
};

export const updateBankStatement = (body: ProspectBankStatement<'payload'>) => {
	return fetcher({
		api: '/surveys/frm/update-bank-statement',
		body,
	});
};

export const deleteBankStatement = (id: number) => {
	return fetcher({
		api: '/surveys/frm/delete-bank-statement',
		body: {
			id,
		},
	});
};

export const submitPraSurvey = (body: { prospectId: string }) => {
	return fetcher({
		api: '/surveys/frm/confirm-pra-survey-data',
		body,
	});
};

export type RequestSurveyPayload = {
	prospectId: string;
	officeAddress: string;
	officeLat: number;
	officeLong: number;
	surveyOfficeDate: string;
	siteAddress: string;
	siteLat: number;
	siteLong: number;
	surveySiteDate: string;
	participants: SurveyParticipant[];
	notes: string;
};

export const submitRequestSurvey = (body: RequestSurveyPayload) => {
	return fetcher({
		api: '/surveys/frm/request-survey',
		body,
	});
};

export type CCASurveyList = {
	customerGroupName: string;
	customerId: string;
	customerName: string;
	department: string;
	frmName: string;
	no: number;
	npwp: string;
	picName: string;
	prospectCreatedAt: string;
	prospectId: string;
	sectorIndustryName: string;
	industryClass: string;
	status: string;
	reviewStatus: string;
};

export const getCCASurveyList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<CCASurveyList>>({
		api: '/surveys/cca/get-survey-list',
		body: defaultParams,
	});
};

export type GetSurveyDetail = {
	mainInformation: MainInformation;
	customerLegalDocuments: CustomerLegalDocument[];
	customerBusinessDocuments: CustomerBusinessDocument[];
	customerReferralDocuments: CustomerLegalDocument[];
	prospectInformation: {
		existingFinancingLimitInformation: ExistingFinancingLimitInformation[];
		financingApplicationLimitInformation: FinancingApplicationLimitInformation[];
	};
	surveyInformation: {
		department: string;
		officeAddress: string;
		officeLat: number;
		officeLong: number;
		surveyOfficeDate: string;
		siteAddress: string;
		siteLat: number;
		siteLong: number;
		surveySiteDate: string;
		surveyParticipants: [
			{
				id?: number;
				userId: number;
				name: string;
				position: string;
			},
		];
	};
	approvalHistories: ApprovalHistory[];
};

export const getSurveyDetail = (prospectId: string) => {
	return fetcher<GetSurveyDetail>({
		api: '/surveys/get-survey-detail',
		body: {
			prospectId,
		},
	});
};

export type SubmitFBDHApprovalSurveyPayload = {
	prospectId: string;
	approvalStatus: string;
	approvalNotes: string;
};

export const submitFBDHApprovalSurvey = (
	body: SubmitFBDHApprovalSurveyPayload,
) => {
	return fetcher({
		api: '/surveys/fbdh/approval-survey',
		body,
	});
};

export type SubmitCCASurveyAnalysisPayload = {
	prospectId: string;
	notes: string;
	surveySiteDate: string;
	surveyOfficeDate: string;
	financialReportAnalysis: ProspectFinancialReportAnalysis<'payload'>[];
};

export const submitCCASurveyAnalysis = (
	body: SubmitCCASurveyAnalysisPayload,
) => {
	return fetcher({
		api: '/surveys/cca/submit-survey-analysis',
		body,
	});
};

export type CCDHSurveyList = {
	no: number;
	prospectId: string;
	customerId: string;
	customerName: string;
	customerGroupName: string;
	picName: string;
	prospectCreatedAt: string;
	department: string;
	frmName: string;
	sectorIndustryName: string;
	industryClass: string;
	npwp: string;
	status: string;
	reviewStatus: string;
};

export const getCCDHSurveyList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<CCDHSurveyList>>({
		api: '/surveys/ccdh/get-survey-list',
		body: defaultParams,
	});
};

export type SubmitCCDHApprovalSurveyAnalysisPayload = {
	prospectId: string;
	approvalStatus: string;
	approvalNotes: string;
	rejectCategoryId?: number;
};

export const submitCCDHApprovalSurveyAnalysis = (
	body: SubmitCCDHApprovalSurveyAnalysisPayload,
) => {
	return fetcher({
		api: '/surveys/ccdh/approval-survey-analysis',
		body,
	});
};

export const getFHSurveyList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<CCDHSurveyList>>({
		api: '/surveys/fh/get-survey-list',
		body: defaultParams,
	});
};

export const getCHSurveyList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<CCDHSurveyList>>({
		api: '/surveys/ch/get-survey-list',
		body: defaultParams,
	});
};

export const getHeadSurveyDetail = (prospectId: string) => {
	return fetcher<GetSurveyDetail>({
		api: '/surveys/head/get-survey-detail',
		body: {
			prospectId,
		},
	});
};

export type SubmitFHApprovalSurveyPayload = {
	prospectId: string;
	approvalStatus: string;
	approvalNotes: string;
	rejectCategoryId?: number;
};

export const submitFHApprovalSurvey = (body: SubmitFHApprovalSurveyPayload) => {
	return fetcher({
		api: '/surveys/fh/approval-survey',
		body,
	});
};

export type SubmitCHApprovalSurveyPayload = {
	prospectId: string;
	approvalStatus: string;
	approvalNotes: string;
	rejectCategoryId?: number;
	participants: number[];
};

export const submitCHApprovalSurvey = (body: SubmitCHApprovalSurveyPayload) => {
	return fetcher({
		api: '/surveys/ch/approval-survey',
		body,
	});
};

export type PraSurveyAnalysisInformation = {
	mainInformation: MainInformation;
	customerCompanyManagementInformation: CustomerManagementInfoPayload<'json'>[];
	customerCompanyShareholderInformation: CustomerShareholderPayload<'json'>[];
	customerLegalDocuments: CustomerLegalDocument[];
	customerReferralDocuments: CustomerLegalDocument[];
	customerBusinessDocuments: CustomerBusinessDocument[];
	prospectFinancialReports: ProspectFinancialReport[];
	approvalHistories: ApprovalHistory[];
	prospectFinancialReportAnalysis: ProspectFinancialReportAnalysis[];
	surveyInformation: SurveyInformation;
};

export const getPraSurveyAnalysisInformation = (prospectId: string) => {
	return fetcher<PraSurveyAnalysisInformation>({
		api: '/surveys/get-pra-survey-analysis-information',
		body: {
			prospectId,
		},
	});
};

export type GetSurveyDataInformation = {
	mainInformation: MainInformation;
	prospectInformation: {
		existingFinancingLimitInformation: ExistingFinancingLimitInformation[];
		financingApplicationLimitInformation: FinancingApplicationLimitInformation[];
	};
	customerLegalDocuments: CustomerLegalDocument[];
	customerBusinessDocuments: CustomerBusinessDocument[];
	customerReferralDocuments?: CustomerReferralDocument[];
	employmentContracts: ProspectEmploymentContract[];
	prospectBankStatements: ProspectBankStatement[];
	prospectFinancialReports: ProspectFinancialReport[];
	prospectFinancialReportAnalysis: ProspectFinancialReportAnalysis[];
	surveyInformation: SurveyInformation;
	approvalHistories: ApprovalHistory[];
};

export const getSurveyDataInformation = (prospectId: string) => {
	return fetcher<GetSurveyDataInformation>({
		api: '/surveys/get-pra-survey-information',
		body: {
			prospectId,
		},
	});
};

export const submitCCAReturnAnalysis = (body: {
	prospectId: string;
	notes: string;
}) => {
	return fetcher({
		api: '/surveys/cca/return-survey-analysis',
		body,
	});
};
